import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Divider,
  Box,
  Tooltip,
  styled,
} from '@mui/material';
import {
  Dashboard,
  Description,
  Inventory,
  People,
  Category,
  Menu,
  ChevronLeft,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

// Constants for sidebar widths
const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 64;

// Styled Drawer for sidebar
const SidebarDrawer = styled(Drawer)(({ theme, open }) => ({
  width: open ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: open ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
    backgroundColor: '#1a1a2e', // Dark background for modern look
    color: '#e0e0e0', // Light text color
    borderRight: 'none',
    boxShadow: '2px 0 10px rgba(0, 0, 0, 0.3)',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
}));

// Styled toggle button
const ToggleButton = styled(IconButton)(({ theme }) => ({
  margin: '10px',
  color: '#e0e0e0',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

// Styled list item for main menu
const MenuItem = styled(ListItem)(({ theme, isActive }) => ({
  margin: '6px 12px',
  padding: '12px 16px',
  borderRadius: '8px',
  backgroundColor: isActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  '& .MuiListItemIcon-root': {
    color: isActive ? '#fff' : '#b0b0b0',
    minWidth: '40px',
  },
  '& .MuiListItemText-primary': {
    fontWeight: isActive ? 600 : 400,
  },
}));

// Styled list item for nested sub-items
const SubMenuItem = styled(ListItem)(({ theme, isActive }) => ({
  margin: '4px 12px 4px 40px',
  padding: '10px 16px',
  borderRadius: '8px',
  backgroundColor: isActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  '& .MuiListItemIcon-root': {
    color: isActive ? '#fff' : '#b0b0b0',
    minWidth: '40px',
  },
}));

function Sidebar({ open, setOpen }) {
  const [mastersExpanded, setMastersExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if a path is active
  const isActive = (path) => location.pathname === path;

  // Toggle sidebar open/closed
  const toggleSidebar = () => {
    setOpen(!open);
  };

  // Handle Masters section toggle
  const toggleMasters = () => {
    if (!open) {
      setOpen(true); // Expand sidebar if collapsed
      setMastersExpanded(true);
    } else {
      setMastersExpanded(!mastersExpanded);
    }
  };

  // Navigate to a path
  const goTo = (path) => {
    navigate(path);
  };

  // Menu items configuration
  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'OCR Processes', icon: <Description />, path: '/ocr-processes' },
    {
      text: 'Masters',
      icon: <Inventory />,
      hasSubItems: true,
      subItems: [
        { text: 'Vendors', icon: <People />, path: '/masters/vendors' },
        { text: 'Items', icon: <Category />, path: '/masters/items' },
        { text: 'Categories', icon: <Category />, path: '/masters/categories' },
      ],
    },
  ];

  return (
    <SidebarDrawer variant="permanent" open={open}>
      {/* Toggle Button */}
      <Box sx={{ display: 'flex', justifyContent: open ? 'flex-end' : 'center', p: 1 }}>
        <ToggleButton onClick={toggleSidebar}>
          {open ? <ChevronLeft /> : <Menu />}
        </ToggleButton>
      </Box>
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />

      {/* Menu List */}
      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            {/* Main Menu Item */}
            {open ? (
              <MenuItem
                button
                onClick={item.hasSubItems ? toggleMasters : () => goTo(item.path)}
                isActive={item.hasSubItems ? mastersExpanded : isActive(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
                {item.hasSubItems && (mastersExpanded ? <ExpandLess /> : <ExpandMore />)}
              </MenuItem>
            ) : (
              <Tooltip title={item.text} placement="right">
                <MenuItem
                  button
                  onClick={item.hasSubItems ? toggleMasters : () => goTo(item.path)}
                  isActive={item.hasSubItems ? mastersExpanded : isActive(item.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                </MenuItem>
              </Tooltip>
            )}

            {/* Sub-items for Masters */}
            {item.hasSubItems && (
              <Collapse in={mastersExpanded && open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <SubMenuItem
                      key={subItem.text}
                      button
                      onClick={() => goTo(subItem.path)}
                      isActive={isActive(subItem.path)}
                    >
                      <ListItemIcon>{subItem.icon}</ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </SubMenuItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </SidebarDrawer>
  );
}

export default Sidebar;